import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_ORIGIN = 'https://www.lyphlinefreezedry.com';

// Upsert a <meta> tag by name or property
const setMetaTag = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLinkTag = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// Main SEO component - sets document title, meta tags and JSON-LD via the DOM
// (avoids react-helmet-async v2 render crashes). Googlebot renders JS so these
// are picked up; index.html provides static defaults for non-JS scrapers.
export const SEO = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  keywords,
  article,
  product,
  recipe,
  faq
}) => {
  const [settings, setSettings] = useState(null);
  const siteName = 'LyphLine';
  const defaultTitle = 'LyphLine Freeze Dryers | Rebuilds, Modifications & Diagnostics';
  const defaultDescription = 'Expert rebuild, modification and diagnostic services for freeze dryers of select makes and varying sizes. Get a free diagnostic quote from LyphLine Freeze Dryers.';
  const defaultImage = '';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      setSettings(res.data);
    } catch (e) {
      // Settings not available
    }
  };

  const pageTitle = title ? `${title} | ${siteName}` : settings?.meta_title || defaultTitle;
  const pageDescription = description || settings?.meta_description || defaultDescription;
  const pageImage = image || defaultImage;
  const pagePath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const cleanPath = pagePath === '/' ? '/' : pagePath.replace(/\/+$/, '');
  const pageUrl = url || `${SITE_ORIGIN}${cleanPath}`;
  const pageKeywords = keywords || settings?.meta_keywords || 'freeze dryer rebuild, freeze dryer modification, freeze dryer service, freeze dryer diagnostics, LyphLine';

  // Apply document title + meta tags
  useEffect(() => {
    document.title = pageTitle;
    setMetaTag('name', 'description', pageDescription);
    setMetaTag('name', 'keywords', pageKeywords);
    setMetaTag('name', 'robots', 'index, follow');
    setLinkTag('canonical', pageUrl);

    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:site_name', siteName);
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', pageDescription);
    setMetaTag('property', 'og:url', pageUrl);
    setMetaTag('property', 'og:image', pageImage);

    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', pageDescription);
    setMetaTag('name', 'twitter:image', pageImage);
  }, [pageTitle, pageDescription, pageKeywords, pageUrl, pageImage, type]);

  // Structured data (JSON-LD)
  useEffect(() => {
    // Clean up previous schema scripts
    const existingScripts = document.querySelectorAll('script[data-schema="dfreeze"]');
    existingScripts.forEach(script => script.remove());

    const origin = SITE_ORIGIN;

    // Organization schema
    const orgSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "LyphLine Freeze Dryers",
      "url": origin,
      "logo": `${origin}/lyphline-icon.png`,
      "image": `${origin}/lyphline-og.jpg`,
      "description": "Expert rebuild, modification and diagnostic services for freeze dryers of select makes and varying sizes",
      "telephone": "+1-888-223-1435",
      "email": "service@lyphlinefreezedry.com",
      "areaServed": ["CA", "US"],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-888-223-1435",
        "contactType": "customer service",
        "email": "service@lyphlinefreezedry.com",
        "areaServed": ["CA", "US"],
        "availableLanguage": ["English"]
      },
      "sameAs": [
        settings?.social_facebook,
        settings?.social_instagram,
        settings?.social_youtube,
        settings?.social_twitter
      ].filter(Boolean)
    };

    // Service schema (freeze dryer rebuild/repair)
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Freeze dryer rebuild, retrofit and diagnostics",
      "provider": { "@type": "Organization", "name": "LyphLine Freeze Dryers", "url": origin, "telephone": "+1-888-223-1435" },
      "areaServed": ["CA", "US"],
      "description": "Diagnose, rebuild, retrofit and modify freeze dryers of select makes and sizes — from large home units to semi commercial machines — with a 90-day rebuild warranty."
    };

    // Website schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "LyphLine Freeze Dryers",
      "url": origin
    };

    // Build schemas array
    const schemas = [orgSchema, websiteSchema, serviceSchema];

    // Product schema
    if (product) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.images || [product.image_url],
        "brand": { "@type": "Brand", "name": "LyphLine" },
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "USD",
          "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
        }
      });
    }

    // Article schema
    if (article) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt || article.description,
        "image": article.featured_image,
        "datePublished": article.created_at,
        "author": { "@type": "Person", "name": article.author?.name || "LyphLine Team" },
        "publisher": { "@type": "Organization", "name": "LyphLine" }
      });
    }

    // Recipe schema
    if (recipe) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Recipe",
        "name": recipe.title,
        "description": recipe.description,
        "image": recipe.featured_image,
        "author": { "@type": "Organization", "name": "LyphLine" },
        "prepTime": recipe.prep_time ? `PT${recipe.prep_time}M` : undefined,
        "cookTime": recipe.cook_time ? `PT${recipe.cook_time}M` : undefined,
        "recipeYield": recipe.servings ? `${recipe.servings} servings` : undefined,
        "recipeCategory": recipe.categories?.[0],
        "keywords": recipe.tags?.join(', '),
        "recipeIngredient": recipe.ingredients?.map(i => `${i.amount}${i.unit ? ' ' + i.unit : ''} ${i.name}`),
        "recipeInstructions": recipe.instructions?.map((step, i) => ({
          "@type": "HowToStep",
          "position": i + 1,
          "text": step
        }))
      });
    }

    // FAQ schema (only if no product/article/recipe)
    if (!product && !article && !recipe) {
      const faqEntities = (faq && faq.length)
        ? faq.map((item) => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": { "@type": "Answer", "text": item.a }
          }))
        : [
          {
            "@type": "Question",
            "name": "Do you rebuild all freeze dryer brands?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. LyphLine services freeze dryers of select makes and models, from large home units to semi commercial machines."
            }
          },
          {
            "@type": "Question",
            "name": "How does the rebuild process work?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Start with a free diagnostic quote. We assess your machine, send a written report and firm quote, and once approved our technicians rebuild or modify it and return it running like new."
            }
          }
        ];
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqEntities
      });
    }

    // Add schema scripts to head
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dfreeze');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup on unmount
      const scripts = document.querySelectorAll('script[data-schema="dfreeze"]');
      scripts.forEach(script => script.remove());
    };
  }, [settings, product, article, recipe, faq]);

  return null;
};

// Google Analytics component - injects GA script via useEffect to avoid Helmet issues
export const GoogleAnalytics = () => {
  const [gaId, setGaId] = useState(null);

  useEffect(() => {
    const fetchGaId = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data?.google_analytics_id) {
          setGaId(res.data.google_analytics_id);
        }
      } catch (e) {
        // Settings not available
      }
    };
    fetchGaId();
  }, []);

  useEffect(() => {
    if (!gaId) return;

    // Remove existing GA scripts
    const existingGaScript = document.getElementById('ga-script');
    const existingGaInline = document.getElementById('ga-inline');
    if (existingGaScript) existingGaScript.remove();
    if (existingGaInline) existingGaInline.remove();

    // Add GA script tag
    const gaScript = document.createElement('script');
    gaScript.id = 'ga-script';
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(gaScript);

    // Add inline GA config
    const gaInline = document.createElement('script');
    gaInline.id = 'ga-inline';
    gaInline.textContent = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${gaId}');`;
    document.head.appendChild(gaInline);

    return () => {
      const script1 = document.getElementById('ga-script');
      const script2 = document.getElementById('ga-inline');
      if (script1) script1.remove();
      if (script2) script2.remove();
    };
  }, [gaId]);

  // Don't render anything - scripts are added via useEffect
  return null;
};

export default SEO;
